import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import {
  Truck, Package, Clock, CheckCircle, MapPin, Phone, Building2,
  Navigation, QrCode, X, ScanLine, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// State machine the driver sees (3 steps, per Feb 2026 spec).
// `ready_for_pickup` is what the system calls it after the vendor
// hits "Ready"; we surface it as "Assigned" to the driver since
// that's what's intuitive for them.
const STATUS_META = {
  ready_for_pickup: { label: 'Assigned', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  picked_up: { label: 'Picked Up', color: 'bg-green-500/20 text-green-400', icon: Package },
  delivered: { label: 'Delivered', color: 'bg-teal-500/20 text-teal-400', icon: CheckCircle },
};

const formatAddress = (pa, fallbackCity) => {
  if (!pa) return fallbackCity || '—';
  if (typeof pa === 'string') return pa;
  const parts = [pa.warehouse_name, pa.line1, pa.line2, pa.city, pa.state, pa.pincode, pa.country]
    .filter(Boolean);
  return parts.join(', ') || fallbackCity || '—';
};

const MarketplacePickupsPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ total: 0, ready_for_pickup: 0, picked_up: 0 });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const loadTasks = async () => {
    try {
      const r = await axios.get(`${API}/marketplace-pickups/my-tasks`, { headers: auth() });
      setTasks(r.data?.tasks || []);
      setSummary(r.data?.summary || { total: 0, ready_for_pickup: 0, picked_up: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pickup tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/marketplace-pickups/my-tasks`, { headers: auth() });
        if (cancelled) return;
        setTasks(r.data?.tasks || []);
        setSummary(r.data?.summary || { total: 0, ready_for_pickup: 0, picked_up: 0 });
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load pickup tasks');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handler invoked by the scanner once a QR text is decoded.
  // The same `_qr_data_uri` helper on the backend embeds `po=<id>|order=<no>`
  // so we just forward the raw payload.
  const onScanned = async (qrPayload) => {
    // Close the scanner FIRST so its html5-qrcode cleanup runs before
    // we kick off the API call. Bug Feb 2026: the prior order had the
    // scanner's unmount-stop race with the in-callback stop on line
    // 290 → html5-qrcode threw an uncaught error → page went blank.
    setScannerOpen(false);
    try {
      const r = await axios.post(
        `${API}/marketplace-pickups/scan`,
        { qr_payload: qrPayload },
        { headers: auth() },
      );
      if (r.data?.already_picked) {
        toast.info(`Already picked: ${r.data.po?.order_number || ''}`);
      } else {
        toast.success(`Marked picked: ${r.data.po?.order_number || ''}`);
      }
      await loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    }
  };

  const markDelivered = async (poId) => {
    setUpdatingId(poId);
    try {
      await axios.post(`${API}/marketplace-pickups/${poId}/mark-delivered`, {}, { headers: auth() });
      toast.success('Marked delivered to warehouse');
      loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="marketplace-pickups-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-playfair text-wt-text-primary flex items-center gap-2">
            <Truck className="w-6 h-6 text-wt-gold" />
            Marketplace Pickups
          </h1>
          <p className="text-wt-text-muted text-sm">Scan the QR sticker on the vendor&apos;s pickup tag to auto-mark picked.</p>
        </div>
        <button
          onClick={loadTasks}
          className="text-xs text-wt-text-muted hover:text-wt-text-primary inline-flex items-center gap-1"
          data-testid="mp-pickups-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        {[
          { label: 'Total', value: summary.total, color: 'text-wt-gold' },
          { label: 'To Pick Up', value: summary.ready_for_pickup, color: 'text-blue-400' },
          { label: 'Picked', value: summary.picked_up, color: 'text-green-400' },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-3"
            data-testid={`mp-pickups-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <p className="text-xs text-wt-text-muted">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Big scan CTA — primary driver action */}
      <Button
        onClick={() => setScannerOpen(true)}
        className="w-full h-14 bg-wt-gold hover:bg-wt-gold-hover text-black text-base font-semibold gap-2"
        data-testid="mp-pickups-open-scanner"
      >
        <ScanLine className="w-5 h-5" />
        Scan Pickup QR
      </Button>

      {/* Task list */}
      {tasks.length === 0 ? (
        <div className="bg-wt-paper border border-wt-border rounded-xl p-8 text-center text-wt-text-muted">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No marketplace pickups assigned to you right now.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="mp-pickups-list">
          {tasks.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.ready_for_pickup;
            const Icon = meta.icon;
            const address = formatAddress(t.pickup_address, t.vendor_city);
            const mapHref = t.pickup_lat && t.pickup_lng
              ? `https://www.google.com/maps/dir/?api=1&destination=${t.pickup_lat},${t.pickup_lng}`
              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-4"
                data-testid={`mp-pickups-task-${t.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-wt-gold" />
                    <span className="font-semibold text-wt-text-primary">{t.vendor_name}</span>
                  </div>
                  <Badge className={`${meta.color} border-0 inline-flex items-center gap-1`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </Badge>
                </div>
                <p className="text-xs text-wt-gold font-mono mb-2">{t.order_number}</p>
                <div className="text-sm text-wt-text-primary mb-2 flex items-start gap-1">
                  <MapPin className="w-3.5 h-3.5 text-wt-text-muted mt-0.5 shrink-0" />
                  <span>{address}</span>
                </div>
                {t.vendor_phone && (
                  <div className="text-sm text-wt-text-muted mb-2 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    <a href={`tel:${t.vendor_phone}`} className="hover:underline">{t.vendor_phone}</a>
                    {t.vendor_contact_person && <span className="ml-1">· {t.vendor_contact_person}</span>}
                  </div>
                )}
                <div className="text-xs text-wt-text-muted mb-3">
                  {t.line_items?.length || 0} item(s) · qty {t.total_quantity}
                  {t.subtotal_cost && typeof formatCurrency === 'function' && (
                    <span className="ml-2 text-wt-gold">· {formatCurrency(t.subtotal_cost)}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={mapHref}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-xs inline-flex items-center gap-1"
                    data-testid={`mp-pickups-nav-${t.id}`}
                  >
                    <Navigation className="w-3 h-3" /> Navigate
                  </a>
                  {t.status === 'ready_for_pickup' && (
                    <Button
                      onClick={() => setScannerOpen(true)}
                      size="sm"
                      className="h-7 px-3 bg-wt-gold hover:bg-wt-gold-hover text-black text-xs"
                      data-testid={`mp-pickups-scan-${t.id}`}
                    >
                      <QrCode className="w-3 h-3 mr-1" /> Scan to pick
                    </Button>
                  )}
                  {t.status === 'picked_up' && (
                    <Button
                      onClick={() => markDelivered(t.id)}
                      disabled={updatingId === t.id}
                      size="sm"
                      className="h-7 px-3 bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 text-xs"
                      data-testid={`mp-pickups-deliver-${t.id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {updatingId === t.id ? 'Saving…' : 'Mark Delivered'}
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {scannerOpen && (
        <QRScannerDialog onClose={() => setScannerOpen(false)} onScanned={onScanned} />
      )}
    </div>
  );
};

// ────────────  QR scanner dialog (uses html5-qrcode + device camera)  ─────────

const QRScannerDialog = ({ onClose, onScanned }) => {
  const containerId = 'mp-pickups-qr-region';
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualText, setManualText] = useState('');

  useEffect(() => {
    let html5QrCode;
    let cancelled = false;
    let started = false;

    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        html5QrCode = new Html5Qrcode(containerId);
        scannerRef.current = html5QrCode;
        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            // Stop immediately so we don't fire multiple times.
            // We `await` the stop BEFORE invoking onScanned so the
            // unmount-cleanup race (Feb 2026 bug — caused a blank
            // screen) cannot happen.
            if (html5QrCode && started) {
              started = false;
              html5QrCode
                .stop()
                .catch(() => {})
                .then(() => { try { html5QrCode.clear?.(); } catch (_e) { /* noop */ } })
                .finally(() => { if (!cancelled) onScanned(decoded); });
            } else if (!cancelled) {
              onScanned(decoded);
            }
          },
          () => { /* per-frame fail, ignore */ },
        );
        started = true;
      } catch (e) {
        setError(e?.message || 'Camera not available. You can paste the QR text instead.');
      }
    })();

    return () => {
      cancelled = true;
      // Only stop if the start handshake completed. Calling stop on a
      // half-initialised instance throws inside html5-qrcode and that
      // unhandled exception is what was bubbling up as the blank
      // screen.
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (inst && started) {
        started = false;
        Promise.resolve(inst.stop())
          .catch(() => {})
          .then(() => { try { inst.clear?.(); } catch (_e) { /* noop */ } });
      }
    };
  }, [onScanned]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="mp-pickups-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <QrCode className="w-5 h-5 text-wt-gold" />
            Scan Pickup QR
          </DialogTitle>
        </DialogHeader>
        {!manualMode ? (
          <>
            <div
              id={containerId}
              className="rounded-lg overflow-hidden bg-black aspect-square mt-2"
              data-testid="mp-pickups-qr-region"
            />
            {error ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-xs text-amber-300 mt-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            ) : (
              <p className="text-xs text-wt-text-muted mt-2">
                Point the rear camera at the QR sticker on the pickup tag. We&apos;ll auto-mark it as picked.
              </p>
            )}
            <button
              onClick={() => setManualMode(true)}
              className="text-xs text-wt-gold hover:underline mt-2"
              data-testid="mp-pickups-manual-toggle"
            >
              Camera not working? Enter QR code manually
            </button>
          </>
        ) : (
          <div className="space-y-2 mt-2">
            <p className="text-xs text-wt-text-muted">
              Paste the QR text. It looks like <span className="font-mono">po=&lt;id&gt;|order=&lt;number&gt;</span>.
            </p>
            <textarea
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm font-mono"
              data-testid="mp-pickups-manual-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setManualMode(false)} className="text-wt-text-muted">
                Back to camera
              </Button>
              <Button
                onClick={() => onScanned(manualText.trim())}
                disabled={!manualText.trim()}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                data-testid="mp-pickups-manual-submit"
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
        <div className="flex justify-end mt-2">
          <button
            onClick={onClose}
            className="text-xs text-wt-text-muted hover:text-wt-text-primary inline-flex items-center gap-1"
            data-testid="mp-pickups-scanner-close"
          >
            <X className="w-3 h-3" /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarketplacePickupsPage;
